import { useSearchParams } from "react-router";

// Reporting periods are rolling windows: the last 30, 60 or 90 days with
// today included, compared against the same number of days just before. The
// delivery reports keep calendar months and quarters (`?compare=`). Keep in
// sync with server/src/lib/dateRange.ts.
export type Period = "30d" | "60d" | "90d";

export const PERIOD_OPTIONS: Array<{ value: Period; label: string; short: string }> = [
  { value: "30d", label: "Last 30 days vs. the 30 before", short: "30 days" },
  { value: "60d", label: "Last 60 days vs. the 60 before", short: "60 days" },
  { value: "90d", label: "Last 90 days vs. the 90 before", short: "90 days" },
];

export function isPeriod(value: unknown): value is Period {
  return value === "30d" || value === "60d" || value === "90d";
}

/** Maps old "mom" / "qoq" / "yoy" bookmarks and other "Nd" values onto the closest window. */
export function toPeriod(range?: string | null): Period {
  if (isPeriod(range)) return range;
  if (range === "mom") return "30d";
  if (range === "qoq" || range === "yoy") return "90d";
  const days = parseInt(String(range ?? "").replace("d", ""), 10);
  if (!Number.isFinite(days) || days <= 45) return "30d";
  if (days <= 75) return "60d";
  return "90d";
}

export interface PeriodWindow {
  period: Period;
  from: Date;
  to: Date;
  prevFrom: Date;
  prevTo: Date;
  /** "Last 30 days" */
  label: string;
  /** "the prior 30 days" */
  prevLabel: string;
  /** "vs. the prior 30 days" */
  vsLabel: string;
  days: number;
}

export function periodDays(period: Period): number {
  return period === "30d" ? 30 : period === "60d" ? 60 : 90;
}

function daysBefore(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - n);
}

export function periodWindow(period: Period, now = new Date()): PeriodWindow {
  const days = periodDays(period);
  const from = daysBefore(now, days - 1);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  const prevFrom = daysBefore(from, days);
  const prevTo = new Date(from.getTime() - 1);
  return {
    period,
    from,
    to,
    prevFrom,
    prevTo,
    days,
    label: `Last ${days} days`,
    prevLabel: `the prior ${days} days`,
    vsLabel: `vs. the prior ${days} days`,
  };
}

/**
 * The page's reporting period, read from `?range=`. The param name stays
 * `range` so every existing API endpoint keeps working; the server parses
 * "30d" / "60d" / "90d" through parsePeriod.
 */
export function usePeriod() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = toPeriod(searchParams.get("range"));
  const setPeriod = (next: Period) =>
    setSearchParams((prev) => {
      prev.set("range", next);
      return prev;
    });
  return { period, range: period, setPeriod, window: periodWindow(period) };
}
